import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getExpenses } from '@/services/expenseService';
import { formatCurrency, getLastNMonths, formatMonthYear } from '@/utils/formatters';
import { DEFAULT_CATEGORIES } from '@/constants';
import LoadingSpinner from '@/components/shared/LoadingSpinner';

const CAT_MAP = Object.fromEntries(DEFAULT_CATEGORIES.map(c => [c.id, c]));

export default function CategoryDeepDive() {
  const [selectedCat, setSelectedCat] = useState('grocery');
  const [trend, setTrend] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const months = getLastNMonths(6).reverse();

  useEffect(() => {
    if (!selectedCat) return;
    setLoading(true);
    Promise.all(
      months.map(m =>
        getExpenses({ month: m, category: selectedCat }).then(exps => ({
          month: m,
          total: exps.reduce((s, e) => s + e.amount, 0),
          expenses: exps,
        }))
      )
    ).then(results => {
      setTrend(results.map(r => ({ name: r.month.slice(5), total: r.total })));
      // Top 5 vendors
      const vendorMap = {};
      for (const r of results) {
        for (const e of r.expenses) {
          vendorMap[e.vendor] = (vendorMap[e.vendor] ?? 0) + e.amount;
        }
      }
      const sorted = Object.entries(vendorMap)
        .map(([v, t]) => ({ vendor: v, total: t }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
      setTopVendors(sorted);
    }).finally(() => setLoading(false));
  }, [selectedCat]);

  const cat = CAT_MAP[selectedCat];

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-xs font-bold text-gray-600 mb-2">📂 Category Deep Dive</h3>
        <select
          value={selectedCat}
          onChange={e => setSelectedCat(e.target.value)}
          className="input-field text-sm"
          aria-label="Select category"
        >
          {DEFAULT_CATEGORIES.map(c => (
            <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          <div className="card">
            <h3 className="text-xs font-bold text-gray-600 mb-3">
              {cat?.emoji} {cat?.label} — 6 Month Trend
            </h3>
            <ResponsiveContainer width="100%" height={130}>
              <LineChart data={trend}>
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 8 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Line type="monotone" dataKey="total" stroke={cat?.color ?? '#1565C0'}
                  strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {topVendors.length > 0 && (
            <div className="card">
              <h3 className="text-xs font-bold text-gray-600 mb-2">Top Vendors (Last 6 months)</h3>
              <div className="space-y-1.5">
                {topVendors.map(({ vendor, total }) => (
                  <div key={vendor} className="flex justify-between text-xs py-1 border-b border-gray-50">
                    <span className="text-gray-700">{vendor}</span>
                    <span className="font-semibold text-primary">{formatCurrency(total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
